import { useEffect, useRef } from "react";

const VERSION_POLL_INTERVAL_MS = 3 * 60 * 1000; // 3 minutes

export function useAutoUpdate() {
  const currentBuildTimeRef = useRef<number | null>(null);

  useEffect(() => {
    let isCancelled = false;

    const checkVersion = async () => {
      try {
        const response = await fetch(`/version.json?_t=${Date.now()}`, {
          cache: "no-store",
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        });

        if (!response.ok || isCancelled) return;

        const data = await response.json();
        const serverBuildTime = data?.buildTime;

        if (!serverBuildTime || typeof serverBuildTime !== "number") return;

        if (currentBuildTimeRef.current === null) {
          // Initialize with current server build time
          currentBuildTimeRef.current = serverBuildTime;
          localStorage.setItem("app_build_time", String(serverBuildTime));
        } else if (serverBuildTime > currentBuildTimeRef.current) {
          console.info("[AutoUpdate] New build detected:", serverBuildTime, "reloading...");
          localStorage.setItem("app_build_time", String(serverBuildTime));
          window.location.reload();
        }
      } catch (err) {
        // Silently ignore network failures when polling version
      }
    };

    // Initialize from localStorage if exists
    const stored = localStorage.getItem("app_build_time");
    if (stored) {
      currentBuildTimeRef.current = Number(stored);
    }

    // Check version once on mount
    checkVersion();

    // Check periodically
    const intervalId = setInterval(checkVersion, VERSION_POLL_INTERVAL_MS);

    // Check when user resumes the app / tabs back into it
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        checkVersion();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleVisibilityChange);

    return () => {
      isCancelled = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleVisibilityChange);
    };
  }, []);
}
